import {
  AccountProductEnum,
  AccountTypeEnum,
  ExternalPlaidAccount,
  InternalAdvanceAccount,
  InternalChargeCardAccount,
  InternalCheckingAccount,
  InternalSavingsAccount,
} from '@varo/graphql-schema-types';
import { setStringAsync } from 'expo-clipboard';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const HairLineDividerWrapper = () => (
  <View style={styles.hairLineDivider}>
    <HairLineDivider />
  </View>
);

interface AccountNumbersProps {
  accountDetails:
    | InternalCheckingAccount
    | InternalSavingsAccount
    | InternalAdvanceAccount
    | ExternalPlaidAccount
    | InternalChargeCardAccount;
  onCopyPress: () => void;
}

const AccountNumbers = ({
  accountDetails,
  onCopyPress,
}: AccountNumbersProps) => {
  // show account numbers hooks
  const [showAccountNumber, setShowAccountNumber] = useState(false);

  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'manageAccount',
  });

  const copyToClipBoard = (text: string) => {
    void setStringAsync(text);
    onCopyPress();
    //TODO: Add Analytics (log events)
  };

  const showAccountNumbers =
    accountDetails.type === AccountTypeEnum.External ||
    accountDetails.product === AccountProductEnum.Checking ||
    accountDetails.product === AccountProductEnum.Savings;

  const accountType =
    accountDetails.type === AccountTypeEnum.External
      ? accountDetails.type
      : accountDetails.product;

  if (!showAccountNumbers) {
    return null;
  }
  return (
    <View>
      <ListItem
        primaryCopy={
          showAccountNumber
            ? ((accountDetails?.fullAccountNumber as string) ?? '')
            : (accountDetails.accountNumber ?? '')
        }
        secondaryCopy={translatedText('accountNumber')}
        lineFormat={ListItemLineFormat.LABEL}
        showsChevron={false}
        analyticsId="accountNumberCopy"
        customAttributes={{ category: accountType.toLowerCase() }}
        trailingAccessory={{
          id: '1',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'COPY',
            iconAccessibilityLabel: 'accountNumber-copy-icon',
          },
        }}
        onPress={() =>
          copyToClipBoard((accountDetails?.fullAccountNumber as string) ?? '')
        }
      />
      <HairLineDividerWrapper />
      <ListItem
        primaryCopy={
          (
            accountDetails as
              | InternalCheckingAccount
              | InternalSavingsAccount
              | ExternalPlaidAccount
          )?.routingNumber ?? ''
        }
        secondaryCopy={translatedText('routingNumber')}
        lineFormat={ListItemLineFormat.LABEL}
        showsChevron={false}
        onPress={() =>
          copyToClipBoard(
            (
              accountDetails as
                | InternalCheckingAccount
                | InternalSavingsAccount
                | ExternalPlaidAccount
            ).routingNumber ?? '',
          )
        }
        trailingAccessory={{
          id: '1',
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'COPY',
            iconAccessibilityLabel: 'routingNumber-copy-icon',
          },
        }}
        analyticsId="routingNumberCopy"
        customAttributes={{ category: accountType.toLowerCase() }}
      />
      <HairLineDividerWrapper />
      <ListItem
        primaryCopy={translatedText('showAccountNumber')}
        showsChevron={false}
        trailingAccessory={{
          id: '1',
          name: VXLComponentId.SWITCH,
          props: {
            isOn: showAccountNumber,
            analyticsId: showAccountNumber
              ? 'showAccountNumberSwitchOff'
              : 'showAccountNumberSwitchOn',
            customAttributes: {
              category: accountType.toLowerCase(),
            },
            onValueChange: () => {
              setShowAccountNumber(!showAccountNumber);
            },
          },
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  hairLineDivider: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
});

export default AccountNumbers;
